'use client';

/**
 * PD-36003 — live-offering adapter for the SE3 editorial private page.
 *
 * The SE3 `PrivatePage` (HomepageV3 editorial "StartEngine Private" surface) is
 * the A/B *test* arm of the legacy `/private` page, so it must render the SAME
 * real private-market offerings the legacy page shows. This hook reuses the
 * legacy private data path exactly:
 *
 *   `useGetPrivateOfferings` → `/elastic-search` (Reg D only) + Strapi
 *   `private-landing-pages` pins/exclusions (the same source the legacy
 *   `components/Private/OfferingsSection` renders)
 *
 * and maps the live offerings into the SE3 private shapes:
 *   - Available shelf  ← open offerings (`status` approved/testing)
 *   - Sold-out shelf   ← closed offerings (`status` closed)
 *   - Spotlight        ← first open offering (featured deal band)
 *
 * Graceful fallback: when the hook is loading / errors / returns empty, the
 * `hasLiveData` flag is false and `PrivatePage` falls back to the static
 * editorial `privateData` content so the shelves/spotlight never blank.
 */

import { useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

import useGetPrivateOfferings from 'hooks/useGetPrivateOfferings';
import { formatCurrencyToShorthand } from 'utils/helpers';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import {
  shouldShowAmountRaised,
  type AmountVisibilityContext,
} from 'utils/offerings/amountRaisedVisibility';
import type { Company, OfferingCardData, Spotlight } from './demoData';
import type { SoldOutCardData } from './privateData';

/** A single live private offering (subset of the elastic-search shape). */
type PrivateOfferingCategory =
  | string
  | {
      name?: string;
      label?: string;
      value?: string;
      title?: string;
      key?: string;
    };

interface PrivateOffering {
  id?: string;
  primaryId?: string;
  primaryOfferingId?: string;
  slug?: string;
  name?: string;
  companyName?: string;
  company_name?: string;
  portfolioCompanyName?: string;
  portfolio_company_name?: string;
  underlyingCompanyName?: string;
  underlying_company_name?: string;
  issuerName?: string;
  series?: string;
  seriesName?: string;
  series_name?: string;
  entityName?: string;
  entity_name?: string;
  company?: {
    name?: string;
    dbaName?: string;
    legalName?: string;
    companyName?: string;
  };
  entity?: {
    name?: string;
    legalName?: string;
  };
  content?: string;
  tagline?: string;
  description?: string;
  elevatorPitch?: string;
  elevator_pitch?: string;
  category?: PrivateOfferingCategory;
  categoryLabel?: string;
  categories?: PrivateOfferingCategory[];
  industry?: PrivateOfferingCategory;
  industryLabel?: string;
  industries?: PrivateOfferingCategory[];
  verticals?: PrivateOfferingCategory[];
  coverTombstoneUrl?: string | null;
  logoUrl?: string | null;
  logo_url?: string | null;
  regulationType?: string;
  amountRaised?: string | number;
  fundsCommitted?: string | number;
  maximumFundingAmount?: string | number;
  maximum_funding_amount?: string | number;
  percentCompleted?: number | string;
  percent_completed?: number | string;
  daysLeft?: number | string;
  days_left?: number | string;
  endDate?: string;
  end_date?: string;
  funding_end_date?: string;
  waitlist?: boolean;
  isWaitlist?: boolean;
  waitlisted?: boolean;
  lockedOnWaitlist?: boolean;
  locked_on_waitlist?: boolean;
  isOverMax?: boolean;
  is_over_max?: boolean;
  spotsLeft?: number | string;
  spots_left?: number | string;
  availability?: string;
  availabilityLabel?: string;
  qpExclusive?: boolean;
  iraExclusive?: boolean;
  soldOut?: boolean;
  status?: string;
}

export interface PrivatePageLiveCompany extends Company {
  /** `/offering/<slug>` link for the live offering. */
  href?: string;
}

export interface PrivatePageOfferings {
  /** Open offerings mapped to "Available Pre-IPO Opportunities". */
  availableCards: OfferingCardData[];
  availableCompanies: Record<string, PrivatePageLiveCompany>;
  /** Closed offerings mapped to "Sold Out Offerings". */
  soldOutCards: SoldOutCardData[];
  soldOutCompanies: Record<string, PrivatePageLiveCompany>;
  /** Featured spotlight derived from the live set (or null when none). */
  spotlight: Spotlight | null;
  spotlightHref?: string;
  spotlightImage?: string;
  /** Slug→href map for card navigation (keyed by `OfferingCardData.co`). */
  hrefByCo: Record<string, string>;
  isLoading: boolean;
  /** True when at least one usable open OR sold-out offering resolved. */
  hasLiveData: boolean;
}

export interface UsePrivatePageOfferingsOptions {
  /** When false, keep the static editorial preview data and skip live private queries. */
  enabled?: boolean;
}

const parseAmount = (value?: string | number): number => {
  if (!value) return NaN;
  return Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
};

const normalizeText = (value?: string): string => (value ?? '').replace(/\s+/g, ' ').trim();

const firstText = (...values: Array<string | undefined | null>): string => {
  for (const value of values) {
    const text = normalizeText(value ?? undefined);
    if (text) return text;
  }
  return '';
};

const sameText = (a?: string, b?: string): boolean =>
  normalizeText(a).toLowerCase() === normalizeText(b).toLowerCase();

const PRIVATE_SERIES_SUFFIX_RE = /(?:\s*,?\s*a series of StartEngine Private LLC)+\s*$/i;
const PRIVATE_SERIES_PREFIX_RE = /^Series\s+/i;
const TEXT_HAS_LETTER_RE = /[a-z]/i;

const normalizeSeriesBase = (value?: string): string =>
  normalizeText(value)
    .replace(PRIVATE_SERIES_SUFFIX_RE, '')
    .replace(/^Series\s+Series\s+/i, 'Series ');

const seriesLabel = (value?: string): string => {
  const text = normalizeSeriesBase(value);
  if (!text) return '';
  if (/^series\s+/i.test(text)) return text;
  return `Series ${text}`;
};

// Funds are named entities in their own right ("Spark Fund 2"), not a single
// portfolio company sold through a Series vehicle — so their tombstone title
// drops the "Series" prefix (Paige QA 2026-07-14). Everything else keeps it.
const FUND_NAME_RE = /\bfunds?\b/i;
const isFundName = (value?: string): boolean => FUND_NAME_RE.test(normalizeSeriesBase(value));

const titleLabel = (value?: string): string => {
  const text = normalizeSeriesBase(value);
  if (!text) return '';
  return isFundName(text) ? text.replace(PRIVATE_SERIES_PREFIX_RE, '') : seriesLabel(text);
};

// Elastic/detail responses sometimes expose the legal internal series number
// (`Series 89-1`) as `name`. That is useful for compliance metadata, but it is
// not a customer-facing tombstone title. Prefer the portfolio-company name in
// that case ("Series Panthalassa"), while retaining meaningful series names
// such as "Series Lambda Labs-3".
const isOpaqueSeriesIdentifier = (value?: string): boolean =>
  /^series\s+\d+(?:[-–—]\d+)*$/i.test(normalizeSeriesBase(value));

const portfolioCompanyLabel = (value?: string): string => {
  const text = normalizeSeriesBase(value);
  if (!PRIVATE_SERIES_PREFIX_RE.test(text)) return text;

  const withoutPrefix = normalizeText(text.replace(PRIVATE_SERIES_PREFIX_RE, ''));
  const withoutOfferingSuffix = normalizeText(
    withoutPrefix
      .replace(/\s*[-–—]\s*QP\s*[-–—]?\s*\d+$/i, '')
      .replace(/\s+QP\s*[-–—]?\s*\d+$/i, '')
      .replace(/\s*[-–—]\s*\d+$/i, '')
      .replace(/\s+\d+$/i, ''),
  );

  if (TEXT_HAS_LETTER_RE.test(withoutOfferingSuffix)) return withoutOfferingSuffix;
  if (TEXT_HAS_LETTER_RE.test(withoutPrefix)) return withoutPrefix;
  return text;
};

const firstDistinctText = (
  candidates: Array<string | undefined | null>,
  blocked: string[],
): string => {
  for (const candidate of candidates) {
    const text = normalizeText(candidate ?? undefined);
    if (!text) continue;
    if (blocked.some((block) => block && sameText(text, block))) continue;
    return text;
  }
  return '';
};

/** Short money string (e.g. "$1.2M") for the "Invested" / "Raised" figure. */
const moneyDisplay = (value?: string | number): string => {
  const amount = parseAmount(value);
  if (!Number.isFinite(amount)) return '';
  return formatCurrencyToShorthand(amount) || '';
};

// The editorial card contract always reserves the left statistic for the
// amount invested.  Some elastic rows omit a raised amount while they are
// being populated; keeping a neutral zero here is preferable to changing the
// card geometry (or moving the right-hand availability figure to the left).
const investedDisplay = (value?: string | number): string => moneyDisplay(value) || '$0';

const portfolioCompanyName = (offering: PrivateOffering): string =>
  portfolioCompanyLabel(
    firstText(
      offering.portfolioCompanyName,
      offering.portfolio_company_name,
      offering.underlyingCompanyName,
      offering.underlying_company_name,
      offering.companyName,
      offering.company_name,
      offering.company?.dbaName,
      offering.company?.name,
      offering.name,
      offering.company?.legalName,
    ),
  );

// The card title uses the campaign-facing offering name (for example,
// "Series Panthalassa"), while the disclosure must use the legal issuer
// series (for example, "Series 89-1, a series of StartEngine Private LLC").
// Keeping these separate prevents the title from degrading into an internal
// series ID and keeps the Reg D disclosure canonical.
const issuerSeriesName = (offering: PrivateOffering): string =>
  firstText(
    offering.issuerName,
    offering.entityName,
    offering.entity_name,
    offering.entity?.legalName,
    offering.entity?.name,
    offering.company?.legalName,
    // Older search-only rows do not include the issuer fields. Fall back to
    // their display name rather than dropping the disclosure entity entirely.
    offering.name,
    offering.companyName,
    offering.company_name,
  );

const categoryText = (category?: PrivateOfferingCategory): string => {
  if (!category) return '';
  if (typeof category === 'string') return normalizeText(category);
  return firstText(category.name, category.label, category.value, category.title, category.key);
};

const firstCategoryText = (
  ...groups: Array<PrivateOfferingCategory | PrivateOfferingCategory[] | string | undefined>
): string => {
  for (const group of groups) {
    if (!group) continue;
    const values = Array.isArray(group) ? group : [group];
    for (const value of values) {
      const text = categoryText(value);
      if (text) return text;
    }
  }
  return '';
};

const industryLine = (offering: PrivateOffering): string =>
  firstCategoryText(
    offering.category,
    offering.categoryLabel,
    offering.categories,
    offering.industry,
    offering.industryLabel,
    offering.industries,
    offering.verticals,
  );

const seriesBaseMatchesCompany = (series: string, companyName: string): boolean =>
  sameText(normalizeSeriesBase(series).replace(PRIVATE_SERIES_PREFIX_RE, ''), companyName);

const distinctSeriesLabel = (series: string, companyName: string): string => {
  if (!series) return '';
  if (sameText(series, companyName)) return '';
  if (seriesBaseMatchesCompany(series, companyName)) return '';
  return seriesLabel(series);
};

const seriesLine = (offering: PrivateOffering): string => {
  const companyName = portfolioCompanyName(offering);
  const explicitSeries = firstText(
    offering.series,
    offering.seriesName,
    offering.series_name,
    offering.entityName,
    offering.entity_name,
    offering.entity?.name,
    offering.entity?.legalName,
    offering.issuerName,
    offering.company?.legalName,
  );
  const fromExplicit = distinctSeriesLabel(explicitSeries, companyName);
  if (fromExplicit) return fromExplicit;

  return distinctSeriesLabel(normalizeText(offering.name), companyName);
};

// Private offerings are legally sold through a Series vehicle.  The tombstone
// title should therefore retain that Series prefix rather than collapsing to
// the underlying portfolio company name (for example, "Series Panthalassa").
// Keep the portfolio-company name separately for the Reg D disclosure.
const cardTitle = (offering: PrivateOffering): string => {
  const companyName = portfolioCompanyName(offering);
  const suppliedName = normalizeSeriesBase(offering.name);
  if (suppliedName && !isOpaqueSeriesIdentifier(suppliedName)) {
    return titleLabel(suppliedName);
  }

  // The detail endpoint can replace the display name with a bare legal series
  // number. Keep the editorial title aligned with the campaign/company name.
  if (companyName && !isOpaqueSeriesIdentifier(companyName)) {
    return titleLabel(companyName);
  }

  const explicitSeries = firstText(
    offering.series,
    offering.seriesName,
    offering.series_name,
    offering.entityName,
    offering.entity_name,
    offering.entity?.name,
    offering.entity?.legalName,
    offering.issuerName,
  );
  return titleLabel(explicitSeries || suppliedName) || companyName;
};

const descriptionLine = (offering: PrivateOffering): string => {
  const companyName = portfolioCompanyName(offering);
  const series = seriesLine(offering);
  const industry = industryLine(offering);
  return firstDistinctText(
    [
      offering.content,
      offering.tagline,
      // `content` is the campaign-facing headline used by the legacy private
      // cards. `elevatorPitch` is the longer campaign intro, so it must only
      // be a fallback; otherwise private cards render a paragraph in their
      // body instead of the campaign headline.
      offering.elevatorPitch,
      offering.elevator_pitch,
      offering.description,
    ],
    [companyName, series, industry],
  );
};

const numericValue = (value?: number | string): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (typeof value === 'string') return parseAmount(value);
  return NaN;
};

const pctComplete = (offering: PrivateOffering): number => {
  // Prefer the truthful raised / ceiling fill, matching the homepage tombstones
  // (PD-36463). percentCompleted is computed off the funding MINIMUM and
  // overflows to a false 100%, so only fall back to it when the raise ceiling
  // (maximumFundingAmount) is absent — this is why private bars were "not
  // working": they relied solely on percentCompleted.
  const raised = numericValue(offering.amountRaised ?? offering.fundsCommitted);
  const max = numericValue(offering.maximumFundingAmount ?? offering.maximum_funding_amount);
  if (Number.isFinite(raised) && Number.isFinite(max) && max > 0) {
    return Math.max(0, Math.min(100, Math.round((raised / max) * 100)));
  }
  // percentCompleted is calculated against the minimum raise and therefore is
  // not a truthful availability measurement. Without a ceiling, show no bar.
  return NaN;
};

const spotsLeft = (offering: PrivateOffering): number => {
  const spots = numericValue(offering.spotsLeft ?? offering.spots_left);
  return Number.isFinite(spots) ? Math.max(0, Math.round(spots)) : NaN;
};

const isWaitlist = (offering: PrivateOffering): boolean =>
  Boolean(
    offering.waitlist ||
    offering.isWaitlist ||
    offering.waitlisted ||
    offering.lockedOnWaitlist ||
    offering.locked_on_waitlist ||
    offering.isOverMax ||
    offering.is_over_max,
  );

const daysLeft = (offering: PrivateOffering): number => {
  const days = numericValue(offering.daysLeft ?? offering.days_left);
  if (Number.isFinite(days)) return Math.round(days);

  const endDate = offering.endDate ?? offering.end_date ?? offering.funding_end_date;
  if (!endDate) return NaN;
  const end = new Date(endDate).getTime();
  if (!Number.isFinite(end)) return NaN;
  const remaining = Math.ceil((end - Date.now()) / 86_400_000);
  return remaining >= 0 ? remaining : NaN;
};

const availabilityDisplay = (offering: PrivateOffering): string => {
  const pct = pctComplete(offering);
  if (isWaitlist(offering)) return 'Waitlist';

  if (Number.isFinite(pct) && pct < 100) {
    const remaining = 100 - pct;
    return `${remaining}% left`;
  }

  // Reg D availability is either calculated from its actual funding ceiling or
  // treated as waitlisted. The source does not expose a usable ceiling for all
  // private series; a neutral dash/empty rail makes those cards look broken and
  // violates the shared tombstone contract. A missing ceiling therefore uses
  // the same full Waitlist state as a fully subscribed raise.
  if (Number.isFinite(pct) && pct >= 100) return 'Waitlist';
  return 'Waitlist';
};

const toBadge = (offering: PrivateOffering): OfferingCardData['badge'] | undefined => {
  // Keep the days-left rule consistent with every other SE3 tombstone. This
  // belongs in the image corner and must not displace the lower availability.
  const days = daysLeft(offering);
  if (Number.isFinite(days) && days >= 0 && days < 30) {
    return { cls: 'bdg-wine', dot: true, label: `${days} ${days === 1 ? 'day' : 'days'} left` };
  }

  if (isWaitlist(offering)) return { cls: 'bdg-gold', label: 'Waitlist' };

  const spots = spotsLeft(offering);
  if (Number.isFinite(spots) && spots > 0) {
    return { cls: 'bdg-wine', dot: true, label: `${spots} ${spots === 1 ? 'spot' : 'spots'} left` };
  }

  return undefined;
};

const toMeta = (offering: PrivateOffering): string | undefined => {
  if (offering.qpExclusive) return 'Qualified Purchasers Exclusive';
  if (offering.iraExclusive) return 'IRA Exclusive';
  if (offering.soldOut) return 'Previously Sold Out';
  return undefined;
};

const OFFERING_IMAGE_CDN = 'https://d19j0qt0x55bap.cloudfront.net';
const ABSOLUTE_OR_DATA_RE = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;

const normalizeCoverUrl = (offering: PrivateOffering): string | undefined => {
  const raw = offering.coverTombstoneUrl?.trim();
  if (!raw) return undefined;
  if (ABSOLUTE_OR_DATA_RE.test(raw) || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) return withSe3AssetPrefix(raw);
  if (raw.startsWith('production/') || raw.startsWith('stage/')) {
    return `${OFFERING_IMAGE_CDN}/${raw}`;
  }

  const image = raw.split('/').pop();
  const primaryId = offering.primaryId || offering.primaryOfferingId;
  if (!primaryId || !image) return raw;

  return `${OFFERING_IMAGE_CDN}/production/startups/${primaryId}/images/startup_cover/${image}`;
};

const normalizeLogoUrl = (offering: PrivateOffering): string | undefined => {
  const raw = (offering.logoUrl ?? offering.logo_url)?.trim();
  if (!raw) return undefined;
  if (ABSOLUTE_OR_DATA_RE.test(raw) || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) return withSe3AssetPrefix(raw);
  if (raw.startsWith('production/') || raw.startsWith('stage/')) {
    return `${OFFERING_IMAGE_CDN}/${raw}`;
  }
  return raw;
};

const toCompany = (
  offering: PrivateOffering,
  amountVisibility: AmountVisibilityContext,
): PrivatePageLiveCompany | null => {
  const key = offering.slug;
  const portfolioName = portfolioCompanyName(offering);
  const name = cardTitle(offering);
  if (!key || !name || !portfolioName) return null;

  const invested = investedDisplay(offering.amountRaised ?? offering.fundsCommitted);
  const showInvested = shouldShowAmountRaised({
    slug: key,
    amountRaised: offering.amountRaised ?? offering.fundsCommitted,
    ...amountVisibility,
  });
  // Preserve both private-offering assets: control uses the company logo and
  // falls back to the cover, while B/C use the tombstone image directly.
  const iconLogo = normalizeLogoUrl(offering);
  const tombstoneImage = normalizeCoverUrl(offering);
  const logo = iconLogo ?? tombstoneImage;
  const description = descriptionLine(offering);

  return {
    key,
    name,
    companyName: issuerSeriesName(offering),
    // The Series is the card title itself. Do not repeat it as an industry or
    // secondary subhead; OfferingCard still renders the Reg D label.
    series: '',
    sector: industryLine(offering),
    invested: showInvested ? invested : '',
    desc: description,
    reg: 'Accredited investors only · See offering page',
    regD: true,
    logo,
    tombstoneImage,
    fit: iconLogo ? 'contain' : 'cover',
    href: `/offering/${key}`,
  };
};

const toAvailableCard = (
  offering: PrivateOffering,
  amountVisibility: AmountVisibilityContext,
): OfferingCardData | null => {
  if (!offering.slug || !portfolioCompanyName(offering)) return null;
  const invested = investedDisplay(offering.amountRaised ?? offering.fundsCommitted);
  const showInvested = shouldShowAmountRaised({
    slug: offering.slug,
    amountRaised: offering.amountRaised ?? offering.fundsCommitted,
    ...amountVisibility,
  });
  const badge = toBadge(offering);
  const meta = toMeta(offering);
  const pct = pctComplete(offering);
  const description = descriptionLine(offering);
  const availability = availabilityDisplay(offering);
  const isWaitlisted = availability === 'Waitlist';
  const urgent = isWaitlisted || (Number.isFinite(pct) && pct >= 85);
  return {
    co: offering.slug,
    ...(description ? { desc: description } : {}),
    ...(badge ? { badge } : {}),
    ...(meta ? { meta } : {}),
    // Keep every private tombstone's data rail structurally identical:
    // invested at left, an availability datum at right, and a progress track.
    ...(showInvested ? { figL: { lbl: 'Invested', val: invested } } : {}),
    figR: { lbl: 'Availability', val: availability, cls: urgent ? 'urg' : 'ok' },
    // A Reg D offering always has an availability state: an actual ceiling
    // produces its truthful fill, otherwise it is represented as waitlisted.
    fill: { w: isWaitlisted ? 100 : Number.isFinite(pct) ? pct : 0, cls: urgent ? 'urg' : 'ok' },
  };
};

const toSoldOutCard = (offering: PrivateOffering): SoldOutCardData | null => {
  if (!offering.slug || !portfolioCompanyName(offering)) return null;
  const invested = investedDisplay(offering.amountRaised ?? offering.fundsCommitted);
  return {
    co: offering.slug,
    meta: 'Closed · Fully subscribed',
    figL: { lbl: 'Invested', val: invested },
    figR: { lbl: 'Availability', val: 'Closed' },
  };
};

const isOpen = (offering: PrivateOffering): boolean =>
  (offering.status === 'approved' || offering.status === 'testing') && !offering.soldOut;
const isClosed = (offering: PrivateOffering): boolean =>
  offering.status === 'closed' || Boolean(offering.soldOut);

/** Build the spotlight from the first open offering with a usable slug + name. */
const pickSpotlight = (
  open: PrivateOffering[],
): { spotlight: Spotlight | null; href?: string; image?: string } => {
  const candidate = open.find((o) => o.slug && portfolioCompanyName(o));
  if (!candidate || !candidate.slug) {
    return { spotlight: null };
  }
  const name = portfolioCompanyName(candidate);
  const description = descriptionLine(candidate);
  return {
    href: `/offering/${candidate.slug}`,
    image: normalizeCoverUrl(candidate),
    spotlight: {
      eyebrowLabel: 'Featured Offering',
      series: seriesLine(candidate),
      name,
      desc: description || `Invest in ${name} on StartEngine Private.`,
      cta: 'View details',
    },
  };
};

export const buildPrivatePageOfferings = (
  offerings: PrivateOffering[] | undefined,
  amountVisibility: AmountVisibilityContext = { settingsLoaded: true },
): Omit<PrivatePageOfferings, 'isLoading'> => {
  const list = offerings ?? [];

  const open = list.filter((o) => isOpen(o));
  const closed = list.filter((o) => isClosed(o));

  const availableCompanies: Record<string, PrivatePageLiveCompany> = {};
  const soldOutCompanies: Record<string, PrivatePageLiveCompany> = {};
  const hrefByCo: Record<string, string> = {};
  const availableCards: OfferingCardData[] = [];
  const soldOutCards: SoldOutCardData[] = [];

  open.forEach((offering) => {
    const company = toCompany(offering, amountVisibility);
    const card = toAvailableCard(offering, amountVisibility);
    if (!company || !card) return;
    availableCompanies[company.key] = company;
    if (company.href) hrefByCo[company.key] = company.href;
    availableCards.push(card);
  });

  closed.forEach((offering) => {
    const company = toCompany(offering, amountVisibility);
    const card = toSoldOutCard(offering);
    if (!company || !card) return;
    soldOutCompanies[company.key] = company;
    if (company.href) hrefByCo[company.key] = company.href;
    soldOutCards.push(card);
  });

  const { spotlight, href: spotlightHref, image: spotlightImage } = pickSpotlight(open);
  const hasLiveData = availableCards.length > 0 || soldOutCards.length > 0;

  return {
    availableCards,
    availableCompanies,
    soldOutCards,
    soldOutCompanies,
    spotlight,
    spotlightHref,
    spotlightImage,
    hrefByCo,
    hasLiveData,
  };
};

/**
 * usePrivatePageOfferings — live private-market offerings for the SE3 editorial
 * private page, mapped into the SE3 shelf/spotlight shapes. Falls back
 * gracefully (consumers use static `privateData`) when `hasLiveData` is false.
 */
export default function usePrivatePageOfferings({
  enabled = true,
}: UsePrivatePageOfferingsOptions = {}): PrivatePageOfferings {
  const searchParams = useSearchParams();
  const partner = searchParams?.get('partner');

  const { data, isLoadingOfferings } = useGetPrivateOfferings({
    approvedOnly: false,
    partners: partner ? [partner] : undefined,
    enabled,
  });
  const { data: showAmountRaisedSlugs, isSuccess: showAmountSettingsLoaded } =
    useShowAmountRaisedList();
  const { data: excludeAmountOfferingSlugs, isSuccess: excludeAmountSettingsLoaded } =
    useExcludeAmountOfferingList();
  const lastStableDataRef = useRef<PrivateOffering[] | undefined>(undefined);
  if (enabled && !isLoadingOfferings && Array.isArray(data)) {
    lastStableDataRef.current = data as PrivateOffering[];
  }

  return useMemo(() => {
    // Keep successful data mounted during a background refresh; replacing it
    // with the static fallback/skeleton was the source of the private-page
    // flicker on load and refetch.
    const stableData = enabled
      ? isLoadingOfferings
        ? lastStableDataRef.current
        : (data as PrivateOffering[] | undefined)
      : undefined;
    const isLoading = enabled && Boolean(isLoadingOfferings) && !stableData;

    return {
      ...buildPrivatePageOfferings(stableData, {
        showAmountRaisedSlugs,
        excludeAmountOfferingSlugs,
        settingsLoaded: showAmountSettingsLoaded && excludeAmountSettingsLoaded,
      }),
      isLoading,
    };
  }, [
    enabled,
    data,
    isLoadingOfferings,
    showAmountRaisedSlugs,
    excludeAmountOfferingSlugs,
    showAmountSettingsLoaded,
    excludeAmountSettingsLoaded,
  ]);
}
